// Formato canônico de mapa (MapDef) — docs/03-MAPS.md.
// Brush convexo = colisão E render a partir do mesmo dado.
import { z } from 'zod';

export const vec3 = z.tuple([z.number(), z.number(), z.number()]);
export type Vec3 = z.infer<typeof vec3>;

/** Plano: n·x = d, normal apontando para FORA do brush */
export const planeSchema = z.object({ n: vec3, d: z.number() });
export type Plane = z.infer<typeof planeSchema>;

export const materialId = z.enum([
  'floor', 'ramp', 'wall', 'accent', 'boost', 'deco', 'glass', 'gold',
]);
export type MaterialId = z.infer<typeof materialId>;

/** Brush no formato canônico: SEMPRE lista de planos (builders emitem isso). */
export const brushSchema = z.object({
  planes: z.array(planeSchema).min(4).max(64), // inclui planos de bevel gerados
  mat: materialId.default('floor'),
  /** rampas surfáveis marcam true — validador exige normal.y < 0.7 na face de surf */
  surf: z.boolean().optional(),
  /** não renderiza (clip/colisão invisível) */
  invisible: z.boolean().optional(),
});
export type BrushDef = z.infer<typeof brushSchema>;

export const zoneType = z.enum([
  'start', 'end', 'stage_start', 'stage_end', 'checkpoint', 'teleport', 'kill', 'push',
]);

export const zoneSchema = z.object({
  type: zoneType,
  min: vec3,
  max: vec3,
  /** stage_start/stage_end/checkpoint */
  index: z.number().int().min(1).optional(),
  /** teleport */
  dest: vec3.optional(),
  destAngles: z.tuple([z.number(), z.number()]).optional(), // [yaw, pitch]
  /** push: velocidade adicionada (uma vez ao entrar) */
  vel: vec3.optional(),
});
export type ZoneDef = z.infer<typeof zoneSchema>;

export const envId = z.enum(['dawn', 'dusk', 'night', 'space', 'sand', 'gold']);
export type MapJsonEnvId = z.infer<typeof envId>; // pipeline JSON (adiado) — EnvId de runtime vive em maps-meta

export const decorSchema = z.object({
  kind: z.string(), // id de asset registrado no loader
  pos: vec3,
  yaw: z.number().default(0),
  scale: z.number().default(1),
});

export const mapSchema = z.object({
  slug: z.string().regex(/^surf_[a-z0-9_]+$/),
  name: z.string(),
  tier: z.number().int().min(1).max(4),
  credit: z.string().optional(), // obrigatório em recriações (mapper original)
  env: envId,
  authorTimeMs: z.number().int().positive(),
  goldMs: z.number().int().positive(),
  silverMs: z.number().int().positive(),
  bronzeMs: z.number().int().positive(),
  stages: z.number().int().min(1),
  spawn: z.object({ pos: vec3, yaw: z.number() }),
  killY: z.number().default(-2048), // abaixo disso = kill implícito
  brushes: z.array(brushSchema).min(1),
  zones: z.array(zoneSchema).min(2), // pelo menos start e end
  decor: z.array(decorSchema).default([]),
});
export type MapDef = z.infer<typeof mapSchema>;

/** Validações além do zod (usadas por validate:maps e pelo compiler em dev) */
export function validateMapLogic(map: MapDef): string[] {
  const errors: string[] = [];
  const starts = map.zones.filter((zn) => zn.type === 'start');
  const ends = map.zones.filter((zn) => zn.type === 'end');
  if (starts.length !== 1) errors.push(`precisa de exatamente 1 zona start (tem ${starts.length})`);
  if (ends.length < 1) errors.push('precisa de pelo menos 1 zona end');
  const st = starts[0];
  if (st) {
    const { pos } = map.spawn;
    const inside =
      pos[0] >= st.min[0] && pos[0] <= st.max[0] &&
      pos[1] >= st.min[1] && pos[1] <= st.max[1] &&
      pos[2] >= st.min[2] && pos[2] <= st.max[2];
    if (!inside) errors.push('spawn precisa estar dentro da zona start');
  }
  for (const zn of map.zones) {
    for (let i = 0; i < 3; i++) {
      if ((zn.min[i] as number) >= (zn.max[i] as number)) {
        errors.push(`zona ${zn.type}: min >= max no eixo ${i}`);
        break;
      }
    }
    if (zn.type === 'teleport' && !zn.dest) errors.push('teleport sem dest');
    if (zn.type === 'push' && !zn.vel) errors.push('push sem vel');
  }
  for (let s = 2; s <= map.stages; s++) {
    if (!map.zones.some((zn) => zn.type === 'stage_start' && zn.index === s)) {
      errors.push(`stages=${map.stages} mas falta stage_start index=${s}`);
    }
  }
  for (const [bi, b] of map.brushes.entries()) {
    if (b.surf && !b.planes.some((p) => Math.abs(p.n[1]) < 0.7 && Math.abs(p.n[1]) > 0.02)) {
      errors.push(`brush ${bi} marcado surf mas nenhuma face tem 0.02<|n.y|<0.7`);
    }
  }
  return errors;
}
